import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card, CardContent } from "../components/ui/card";
import axios from '../axiosInstance';
import logoBanner from "../assets/images/new_logo_without_font.png";
import rollercoinBanner from "../assets/images/bannerRollerCoin.gif";

export default function Terms({ onLogout }) {

  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-950 text-white flex flex-col">
      {/* Header */}
      <header className="border-b border-gray-800 px-6 py-4 flex items-center justify-between">
        <Link to="/index">
		<img src={logoBanner} alt="CoinZUp Logo" className="w-40 h-auto" />
		</Link>
        <div className="space-x-4">
          <Link to="/login" className="text-sm hover:underline">Log In</Link>
          <Link to="/register" className="bg-yellow-500 text-black px-4 py-2 rounded hover:bg-yellow-400 transition">Sign Up</Link>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 max-w-4xl mx-auto px-4 py-6 overflow-auto">
        <h1 className="text-3xl font-bold mb-4 text-center">Terms &amp; Conditions</h1>
        <p className="text-sm text-gray-400 mb-6">Last updated: May 9, 2025</p>

        <section className="mb-6">
          <h2 className="text-xl font-semibold mb-2">1. Introduction</h2>
          <p className="text-gray-300 leading-relaxed">
            Welcome to CoinzUp.net. By accessing or using our services, you agree to be bound by these Terms &amp; Conditions. If you do not agree, please do not use our platform.
          </p>
        </section>

        <section className="mb-6">
          <h2 className="text-xl font-semibold mb-2">2. Eligibility</h2>
          <p className="text-gray-300 leading-relaxed">
            You must be at least 18 years old to use CoinzUp.net. By registering, you confirm that you meet this minimum age requirement.
          </p>
        </section>

        <section className="mb-6">
          <h2 className="text-xl font-semibold mb-2">3. Prohibited Activities</h2>
          <ul className="list-disc list-inside text-gray-300 leading-relaxed space-y-1">
            <li>Using VPNs, proxies, Tor, bots or any automated tools to simulate activity.</li>
            <li>Creating or operating multiple accounts.</li>
            <li>Sharing or selling your account credentials.</li>
            <li>Posting illegal, offensive, or infringing content.</li>
          </ul>
        </section>

        <section className="mb-6">
          <h2 className="text-xl font-semibold mb-2">4. Ways to Earn Coinz &amp; XP</h2>
          <ul className="list-disc list-inside text-gray-300 leading-relaxed space-y-1 mb-4">
            <li><strong>AutoSurf:</strong> Run sessions of at least 1 hour to earn 60 XP and up to 60 COINZ (multiplied by your level). 
			<a href="/levelup" target="_blank" rel="noopener noreferrer" className="underline text-blue-400 hover:text-blue-300">
			  Learn more
			</a>.</li>
            <li><strong>Hourly Draw:</strong> Participate in the free hourly draw on your dashboard to win bonus COINZ.</li>
            <li><strong>Weekly Dividends:</strong> Hold Options and participate in at least 10 hourly draws over the past 30 days to qualify. Otherwise, you risk losing your Options if inactive. You may sell them to avoid this.</li>
          </ul>
        </section>

        <section className="mb-6">
          <h2 className="text-xl font-semibold mb-2">5. Withdrawals</h2>
          <p className="text-gray-300 leading-relaxed">
            Withdrawals are processed automatically each week in BTC. You need a minimum balance of 100,000 COINZ (equivalent to $1) to request a withdrawal. Ensure your BTC wallet address is correct.
          </p>
        </section>

        <section className="mb-6">
          <h2 className="text-xl font-semibold mb-2">6. Advertising &amp; XP Purchases</h2>
          <p className="text-gray-300 leading-relaxed">
            You may purchase advertising slots to gain XP. All submitted URLs are reviewed to ensure they do not belong to prohibited categories (e.g. adult, illegal). The administrator reserves the right to revoke XP without refund of COINZ for any violations.
          </p>
        </section>

        <section className="mb-6">
          <h2 className="text-xl font-semibold mb-2">7. Account Security</h2>
          <p className="text-gray-300 leading-relaxed">
            You are responsible for safeguarding your account credentials. We are not liable for losses resulting from unauthorized access to your account.
          </p>
        </section>

        <section className="mb-6">
          <h2 className="text-xl font-semibold mb-2">8. Termination</h2>
          <p className="text-gray-300 leading-relaxed">
            We reserve the right to suspend or terminate your account at our discretion for violations of these Terms &amp; Conditions, without notice or refund.
          </p>
        </section>

        <section className="mb-6">
          <h2 className="text-xl font-semibold mb-2">9. Amendments</h2>
          <p className="text-gray-300 leading-relaxed">
            We may modify these Terms &amp; Conditions at any time. Changes are effective upon posting. Continued use constitutes acceptance of the revised terms.
          </p>
        </section>

        <section className="mb-6">
          <h2 className="text-xl font-semibold mb-2">10. Contact Us</h2>
          <p className="text-gray-300 leading-relaxed">
            Questions? Reach out via our <Link to="/notice" className="underline text-blue-400 hover:text-blue-300">contact form</Link> or email us at <a href="mailto:contact@coinzup.net" className="underline text-blue-400 hover:text-blue-300">contact@coinzup.net</a>.
          </p>
        </section>

        <section className="mb-6">
        <h2 className="text-xl font-semibold mb-2">
          11. Email Communications and Prospecting
        </h2>
        <p className="leading-relaxed">
          By creating an account on CoinzUp.net, you agree to receive by email
          important information concerning the use of your account, such as
          security notifications, service updates, or information related to
          your activities on the platform.
        </p>
        <p className="leading-relaxed mt-4">
          In addition, with your explicit consent obtained at the time of
          registration or subsequently, you may receive messages of a commercial
          nature, including promotional offers, news about our services, or news
          from CoinzUp.net.
        </p>
        <p className="leading-relaxed mt-4">
          You may at any time withdraw your consent and object to receiving
          these commercial messages by clicking on the unsubscribe link included
          in each email or by contacting us directly at the following address:{" "}
          <a
            href="mailto:contact@coinzup.net"
            className="underline text-blue-400 hover:text-blue-300"
          >
            contact@coinzup.net
          </a>
          .
        </p>
        <p className="leading-relaxed mt-4">
          We are committed to complying with applicable legislation on data
          protection and electronic prospecting.
        </p>
      </section>

      </main>

      {/* Footer */}
		<footer className="bg-gray-900/80 text-center text-sm text-gray-500 py-4 border-t border-gray-800">
		  <p>© 2025 CoinzUp.net. All rights reserved.</p>
		  <p>
			<Link to="/notice" className="hover:text-white underline mr-4">Privacy - Legal Notice</Link>
			<Link to="/terms" className="hover:text-white underline">Terms - Conditions</Link>
		  </p>
		</footer>
    </div>
  );
}